import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { avatarsForSpace } from "../../../utils/avatarCatalog";
import { Space } from "../../../utils/roles";
import { Button } from "../../common/Button";

// AvatarPickerModal : sélection d'un avatar parmi le catalogue par défaut (thème élevage).
// Remplace l'ancien AvatarEditorModal (crop/upload base64) — voir historique FRONT 2026-07.
// onSelect reçoit l'id du catalogue (stocké tel quel dans Account.avatar).
//
// Pagination : 12 avatars par page (grille 4×3). À l'ouverture, on saute directement sur la page
// qui contient l'avatar actuellement sélectionné.
const PER_PAGE = 12;

interface AvatarPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (avatarId: string) => void;
  currentValue?: string | null;
  space: Space; // n'affiche que les avatars proposés à cet espace
}

export const AvatarPickerModal: React.FC<AvatarPickerModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  currentValue,
  space,
}) => {
  const { t } = useTranslation();
  const catalog = useMemo(() => avatarsForSpace(space), [space]);
  const pageCount = Math.ceil(catalog.length / PER_PAGE);
  const [page, setPage] = useState(0);

  // À l'ouverture, se placer sur la page de l'avatar courant (ou la première).
  useEffect(() => {
    if (isOpen) {
      const idx = catalog.findIndex((a) => a.id === currentValue);
      setPage(idx >= 0 ? Math.floor(idx / PER_PAGE) : 0);
    }
  }, [isOpen, currentValue, catalog]);

  if (!isOpen) return null;

  const start = page * PER_PAGE;
  const visible = catalog.slice(start, start + PER_PAGE);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
        <h2 className="text-2xl font-oxaniumMedium mb-4">
          {t("avatar-picker-title")}
        </h2>

        <div className="grid grid-cols-4 gap-3 mb-4">
          {visible.map((avatar) => (
            <button
              key={avatar.id}
              type="button"
              title={avatar.label}
              aria-label={avatar.label}
              onClick={() => {
                onSelect(avatar.id);
                onClose();
              }}
              className={`aspect-square rounded-lg flex items-center justify-center text-3xl transition-transform hover:scale-105 ${
                currentValue === avatar.id
                  ? "ring-4 ring-secondary"
                  : "ring-1 ring-grey-light"
              }`}
              style={{ backgroundColor: avatar.bg }}
            >
              <span role="img" aria-label={avatar.label}>
                {avatar.emoji}
              </span>
            </button>
          ))}
        </div>

        {/* Pagination : ‹ · points · › (chevrons en texte, aucune icône à enregistrer) */}
        {pageCount > 1 && (
          <div className="flex items-center justify-center gap-4 mb-6">
            <button
              type="button"
              aria-label={t("avatar-picker-previous")}
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              className="w-8 h-8 rounded-full flex items-center justify-center text-lg text-tertiary hover:bg-grey-light disabled:opacity-30 disabled:hover:bg-transparent transition"
            >
              ‹
            </button>

            <div className="flex items-center gap-2">
              {Array.from({ length: pageCount }).map((_, i) => (
                <button
                  key={i}
                  type="button"
                  aria-label={`Page ${i + 1}`}
                  aria-current={i === page}
                  onClick={() => setPage(i)}
                  className={`w-2.5 h-2.5 rounded-full transition ${
                    i === page ? "bg-secondary" : "bg-grey-light hover:bg-grey-medium"
                  }`}
                />
              ))}
            </div>

            <button
              type="button"
              aria-label={t("avatar-picker-next")}
              disabled={page === pageCount - 1}
              onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
              className="w-8 h-8 rounded-full flex items-center justify-center text-lg text-tertiary hover:bg-grey-light disabled:opacity-30 disabled:hover:bg-transparent transition"
            >
              ›
            </button>
          </div>
        )}

        <div className="flex justify-end">
          <Button variant="secondary" onClick={onClose}>
            {t("button-close")}
          </Button>
        </div>
      </div>
    </div>
  );
};
